/**
 * Legalito panel for the Authorities tab.
 *
 * Shows the calling solicitor whether THEIR Legalito account is
 * connected (per-user, not firm-wide), the current push-state for the
 * case (un-pushed vs already in Legalito), and a button that either:
 *   - sends the matter to Legalito for the first time (with the
 *     pricing confirmation modal), OR
 *   - opens the existing matter in a new tab.
 *
 * If the solicitor hasn't added their Legalito credentials yet, we
 * gently push them to /profile rather than letting the button fail.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowSquareOut, Receipt, CheckCircle, PaperPlaneTilt, ArrowsClockwise, Plus, Trash, CurrencyGbp,
} from "@phosphor-icons/react";

const VAT_RATE = 0.2;
const PRICE_FMT = (n) => `£${Number(n || 0).toFixed(2)}`;

function SpendTracker({ caseId, spend, pricing, onChange }) {
  const [adding, setAdding] = useState(false);
  const [reportKey, setReportKey] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const items = spend?.entries || [];
  const totals = spend?.totals || { excl_vat: 0, vat: 0, incl_vat: 0 };

  const handleAdd = async () => {
    if (!reportKey) {
      toast.error("Pick which report you ordered");
      return;
    }
    setSaving(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/legalito/spend`, {
        report_key: reportKey,
        notes: notes.trim() || null,
      });
      onChange?.(data);
      setAdding(false);
      setReportKey("");
      setNotes("");
      toast.success("Spend logged");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to log spend");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Remove this spend entry?")) return;
    try {
      const { data } = await API.delete(`/cases/${caseId}/legalito/spend/${entryId}`);
      onChange?.(data);
      toast.success("Entry removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove entry");
    }
  };

  const handleRefreshFromLegalito = async () => {
    setRefreshing(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/legalito/spend/refresh`);
      if (!data.refreshed) {
        toast.info(data.reason || "Refresh not available yet — keep logging manually for now.");
      } else {
        toast.success("Synced from Legalito");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const pricingOptions = pricing?.items || [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="legalito-spend-tracker">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CurrencyGbp weight="duotone" className="w-5 h-5 text-[#1462a0]" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Legalito Report Spend
            </h3>
            <p className="text-[11px] text-slate-500">
              Log each report you order on Legalito so the case file shows the running cost.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefreshFromLegalito}
            disabled={refreshing}
            data-testid="legalito-spend-refresh"
            title="Auto-sync from Legalito (coming soon)"
          >
            <ArrowsClockwise weight="bold" className="w-3.5 h-3.5 mr-1" />
            Sync
          </Button>
          <Button
            size="sm"
            onClick={() => setAdding((v) => !v)}
            className="bg-[#1462a0] hover:bg-[#10507f] text-white"
            data-testid="legalito-spend-add-toggle"
          >
            <Plus weight="bold" className="w-3.5 h-3.5 mr-1" />
            Log report
          </Button>
        </div>
      </div>

      {adding && (
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60" data-testid="legalito-spend-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            <div className="sm:col-span-5">
              <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600 block mb-1">
                Report
              </label>
              <Select value={reportKey} onValueChange={setReportKey}>
                <SelectTrigger className="h-9 text-sm" data-testid="legalito-spend-report-select">
                  <SelectValue placeholder="Pick report ordered…" />
                </SelectTrigger>
                <SelectContent>
                  {pricingOptions.map((p) => (
                    <SelectItem key={p.key} value={p.key} data-testid={`legalito-spend-option-${p.key}`}>
                      {p.label} — {PRICE_FMT(p.amount_excl_vat)} + VAT
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-5">
              <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600 block mb-1">
                Notes (optional)
              </label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. ordered after lender requested SoW"
                className="h-9 text-sm"
                data-testid="legalito-spend-notes-input"
              />
            </div>
            <div className="sm:col-span-2 flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setAdding(false)} disabled={saving} data-testid="legalito-spend-cancel">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={saving || !reportKey}
                className="bg-[#1462a0] hover:bg-[#10507f] text-white"
                data-testid="legalito-spend-save"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="px-5 py-4">
        {items.length === 0 ? (
          <p className="text-xs text-slate-500 italic" data-testid="legalito-spend-empty">
            No reports logged for this case yet.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="text-left py-1.5 font-semibold">Report</th>
                <th className="text-left py-1.5 font-semibold hidden sm:table-cell">Ordered by</th>
                <th className="text-left py-1.5 font-semibold hidden sm:table-cell">Date</th>
                <th className="text-right py-1.5 font-semibold">Cost</th>
                <th className="py-1.5 w-8" />
              </tr>
            </thead>
            <tbody data-testid="legalito-spend-rows">
              {items.map((e) => (
                <tr key={e.id} className="border-b border-slate-50 last:border-b-0" data-testid={`legalito-spend-row-${e.id}`}>
                  <td className="py-1.5">
                    <div className="text-slate-800">{e.label}</div>
                    {e.notes && <div className="text-[11px] text-slate-500">{e.notes}</div>}
                  </td>
                  <td className="py-1.5 text-slate-600 text-xs hidden sm:table-cell">{e.created_by_user_name || "—"}</td>
                  <td className="py-1.5 text-slate-600 text-xs hidden sm:table-cell">
                    {e.created_at ? new Date(e.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </td>
                  <td className="py-1.5 text-right">
                    <span className="font-medium">{PRICE_FMT(e.amount_excl_vat)}</span>
                    <span className="text-[11px] text-slate-400"> + VAT</span>
                  </td>
                  <td className="py-1.5 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                      onClick={() => handleDelete(e.id)}
                      data-testid={`legalito-spend-delete-${e.id}`}
                    >
                      <Trash weight="bold" className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 text-sm font-semibold">
                <td className="py-2" colSpan={3}>Total</td>
                <td className="py-2 text-right" data-testid="legalito-spend-total">
                  <div>{PRICE_FMT(totals.excl_vat)} <span className="text-[11px] text-slate-500">excl.</span></div>
                  <div className="text-[11px] text-slate-500 font-normal">
                    +{PRICE_FMT(totals.vat)} VAT = {PRICE_FMT(totals.incl_vat)} inc
                  </div>
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

export default function LegalitoPanel({ caseId, isSolicitor }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const [caseStatus, setCaseStatus] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [spend, setSpend] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c, p, s] = await Promise.all([
        API.get("/legalito/profile/status").catch(() => ({ data: { configured: false } })),
        API.get(`/cases/${caseId}/legalito`).catch(() => ({ data: { pushed: false } })),
        API.get("/legalito/pricing").catch(() => ({ data: null })),
        API.get(`/cases/${caseId}/legalito/spend`).catch(() => ({ data: null })),
      ]);
      setUserStatus(u.data);
      setCaseStatus(c.data);
      setPricing(p.data);
      setSpend(s.data);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handlePush = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/legalito/open`);
      setCaseStatus({ pushed: true, ...data });
      setConfirmOpen(false);
      // Open in new tab
      window.open(data.deep_link, "_blank", "noopener,noreferrer");
      toast.success(data.already_existed ? "Re-opening existing Legalito matter" : "Matter created in Legalito");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to open in Legalito");
    } finally {
      setBusy(false);
    }
  };

  if (!isSolicitor) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600" data-testid="legalito-panel-noaccess">
        Legalito access is available to solicitors only.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-5 animate-pulse">
        <div className="h-3 w-40 bg-slate-200 rounded mb-2" />
        <div className="h-3 w-64 bg-slate-200 rounded" />
      </div>
    );
  }

  // Supplier-authenticated flow: no per-solicitor Legalito password
  // needed. Legalito identifies the solicitor by their ConveyHub email
  // and mints a one-time sign-in link, so this works for every solicitor.
  const pushed = caseStatus?.pushed;
  return (
    <div className="space-y-4" data-testid="legalito-panel">
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Legalito — Pre-Contract Enquiries
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Opens in Legalito under your account — no separate Legalito login needed.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={refresh} disabled={busy} data-testid="legalito-refresh">
            <ArrowsClockwise weight="bold" className="w-3.5 h-3.5 mr-1" />
            Refresh
          </Button>
        </div>

        <div className="px-5 py-4">
          {pushed ? (
            <div className="space-y-3" data-testid="legalito-case-pushed">
              <div className="flex items-start gap-3 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3">
                <CheckCircle weight="fill" className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-emerald-900">
                    Case is open in Legalito
                  </div>
                  <div className="text-xs text-emerald-800 mt-0.5">
                    {caseStatus.created_by_user_name && (
                      <span>Opened by {caseStatus.created_by_user_name}</span>
                    )}
                    {caseStatus.created_at && (
                      <span> · {new Date(caseStatus.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    )}
                  </div>
                </div>
                <Button
                  onClick={handlePush}
                  disabled={busy}
                  className="bg-[#1462a0] hover:bg-[#10507f] text-white"
                  data-testid="legalito-open-link"
                >
                  <ArrowSquareOut weight="bold" className="w-4 h-4 mr-1.5" />
                  {busy ? "Opening…" : "Open in Legalito"}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500">
                Enquiries raised in Legalito will appear in this case&apos;s document folder automatically as they land.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="legalito-case-not-pushed">
              <p className="text-sm text-slate-700">
                This case hasn&apos;t been opened in Legalito yet. Click below to push the matter — Legalito will create the matter under your account and open the enquiries view in a new tab.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={busy}
                  className="bg-[#1462a0] hover:bg-[#10507f] text-white"
                  data-testid="legalito-push-button"
                >
                  <PaperPlaneTilt weight="bold" className="w-4 h-4 mr-1.5" />
                  Open in Legalito
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pricing reminder card — always visible so partners know what they're spending */}
      {pricing?.items?.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="legalito-pricing">
          <div className="flex items-center gap-2 mb-2">
            <Receipt weight="duotone" className="w-4 h-4 text-slate-600" />
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Report pricing (effective {new Date(pricing.effective_from).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })})
            </h4>
          </div>
          <ul className="text-xs text-slate-700 space-y-1">
            {pricing.items.map((p) => (
              <li key={p.label} className="flex justify-between">
                <span>{p.label}</span>
                <span className="font-medium">
                  {PRICE_FMT(p.amount_excl_vat)} <span className="text-slate-400">+ VAT</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Per-case Spend Tracker — manual entries, totals roll up */}
      {pushed && (
        <SpendTracker
          caseId={caseId}
          spend={spend}
          pricing={pricing}
          onChange={setSpend}
        />
      )}

      {/* Pre-push confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="legalito-confirm-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Push case to Legalito?</DialogTitle>
            <DialogDescription>
              We&apos;ll create the matter on Legalito&apos;s side under your account, link it to this case via our reference, and open the enquiries view in a new tab.
            </DialogDescription>
          </DialogHeader>
          {pricing?.items?.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
              <div className="text-xs font-semibold text-amber-900 mb-1.5">
                Reports you can order on Legalito (charged per report):
              </div>
              <ul className="text-[12px] text-amber-900 space-y-0.5">
                {pricing.items.map((p) => {
                  const inc = p.amount_excl_vat * (1 + VAT_RATE);
                  return (
                    <li key={p.label} className="flex justify-between">
                      <span>{p.label}</span>
                      <span>
                        <span className="font-medium">{PRICE_FMT(p.amount_excl_vat)}</span>
                        <span className="text-amber-700"> + VAT</span>
                        <span className="text-amber-700/70 ml-1">({PRICE_FMT(inc)} inc)</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="text-[10.5px] text-amber-800 mt-1.5 italic">
                You only pay when you order a specific report inside Legalito. Pushing the case itself is free.
              </div>
            </div>
          )}
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="legalito-confirm-cancel">
              Cancel
            </Button>
            <Button
              onClick={handlePush}
              disabled={busy}
              className="bg-[#1462a0] hover:bg-[#10507f] text-white"
              data-testid="legalito-confirm-push"
            >
              <PaperPlaneTilt weight="bold" className="w-4 h-4 mr-1.5" />
              {busy ? "Pushing…" : "Push and open"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
